/* audio-tag-reader.js — read an audio file's TAG FIELDS as structured data.
 *
 * Why this exists (2026-08-06). /ai-detector used to look for AI-generator
 * markers by regex-scanning the first 256 KB + last 64 KB of the RAW FILE as
 * text. Two failure modes, both measured on real files:
 *
 *   1. The scan sees COMPRESSED AUDIO BYTES, not just tags. On 227 verified-
 *      human tracks the shipped rule fired once — on the byte sequence "aIVa"
 *      inside the audio stream, which it reported as "Confirmed AI source: AIVA".
 *   2. It cannot tell WHERE a word sits. "suno" (सुनो, "listen") is an ordinary
 *      Hindi word in hundreds of real song titles; a track titled "Suno Na"
 *      was declared "Generated by AI, 100 %" on production.
 *
 * A brand word in a TITLE is what a human typed. The same word in an ENCODER
 * or a TXXX frame is what a tool wrote about itself. Those are different kinds
 * of evidence, and only structure can tell them apart — hence a parser, not a
 * bigger regex.
 *
 * Field classes returned:
 *   'declaration' — a machine-readable "this is AI" statement. The IPTC term
 *                   digitalsourcetype=trainedAlgorithmicMedia is the standard
 *                   one; it is exactly what OUR OWN AiContentMarker / ai-mark.js
 *                   writes, so reader and writer now speak the same language.
 *   'tool'        — written by software about itself: encoder (TSSE/ISFT),
 *                   comment, TXXX/user-defined, APE "Encoder".
 *   'content'     — typed by a person: title, artist, album, lyrics, genre.
 *
 * Covers mp3 (ID3v2 + ID3v1 + APEv2), wav and aiff (RIFF LIST/INFO + ID3
 * chunk), flac and ogg (Vorbis comment), m4a/mp4 (moov/udta/meta/ilst) — 96.5 %
 * of what /ai-detector actually receives (measured over 4043 uploads, 30 days).
 * Unknown containers return `format:'unknown'` with no fields, and the caller
 * is expected to treat that as "no structured evidence", never as proof.
 *
 * WMA / ASF since 2026-09-24: Content Description, Extended Content
 * Description and the Metadata / Metadata Library objects inside the Header
 * Extension. Until then every WMA came back with no tags at all, so
 * /car-usb-music wrote WMA tracks to the drive titled by their FILE NAME, with
 * no artist or album — the one thing that tool exists to get right. The
 * ES-module twin of this walk is public/js/asf-meta.js; tests/js/asf-meta.test.mjs
 * keeps the two reading the same header the same way.
 *
 * Tags are read WHERE THEY ARE, not only in the head (2026-09-23): the moov of
 * an M4A without faststart is found by walking top-level box headers, and the
 * ID3 / LIST chunks of WAV and AIFF — which sit after the audio data — by
 * walking chunk headers. Before that, every non-faststart M4A and every AIFF
 * came back with no tags at all, for every caller at once.
 *
 * Exposes window.AudioTagReader; also CommonJS-exported so the parser is
 * unit-tested in Node (tests/js/audio-tag-reader-tail-tags.test.mjs).
 */
(function (root, factory) {
    'use strict';
    var mod = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = mod;
    if (typeof window !== 'undefined') window.AudioTagReader = mod;
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    /* ── field classification ───────────────────────────────────────────── */

    // Human-authored. A generator name here is a coincidence or a song title,
    // never a self-report. This is the set that made "Suno Na" a false accusation.
    var CONTENT_FIELDS = {
        // ID3v2.3/2.4
        TIT1: 1, TIT2: 1, TIT3: 1, TPE1: 1, TPE2: 1, TPE3: 1, TPE4: 1, TALB: 1,
        TOAL: 1, TOPE: 1, TCOM: 1, TEXT: 1, TCON: 1, TPUB: 1, USLT: 1, SYLT: 1,
        TOLY: 1, TRCK: 1, TPOS: 1, TDRC: 1, TYER: 1, TCOP: 1,
        // ID3v2.2
        TT1: 1, TT2: 1, TT3: 1, TP1: 1, TP2: 1, TP3: 1, TP4: 1, TAL: 1, TCM: 1,
        TCO: 1, ULT: 1, TRK: 1, TYE: 1,
        // Vorbis / APE / RIFF / MP4 (normalised upper-case names)
        TITLE: 1, ARTIST: 1, ALBUM: 1, ALBUMARTIST: 1, COMPOSER: 1, PERFORMER: 1,
        LYRICS: 1, GENRE: 1, DATE: 1, TRACKNUMBER: 1, PUBLISHER: 1, COPYRIGHT: 1,
        INAM: 1, IART: 1, IPRD: 1, IGNR: 1, ICOP: 1,
        '©NAM': 1, '©ART': 1, '©ALB': 1, '©WRT': 1, '©GEN': 1,
        '©DAY': 1, '©LYR': 1,
        // ASF (WMA): Content Description names + the WM/* attributes people type.
        AUTHOR: 1, RATING: 1, 'WM/ALBUMTITLE': 1, 'WM/ALBUMARTIST': 1, 'WM/GENRE': 1,
        'WM/YEAR': 1, 'WM/TRACKNUMBER': 1, 'WM/TRACK': 1, 'WM/PARTOFSET': 1, 'WM/COMPOSER': 1,
        'WM/LYRICS': 1, 'WM/WRITER': 1, 'WM/CONDUCTOR': 1, 'WM/PUBLISHER': 1,
        'WM/ORIGINALARTIST': 1, 'WM/ORIGINALALBUMTITLE': 1, 'WM/SUBTITLE': 1
    };

    // Written by software about itself.
    var TOOL_FIELDS = {
        TSSE: 1, TENC: 1, TSS: 1, TEN: 1, COMM: 1, COM: 1, TXXX: 1, TXX: 1,
        WXXX: 1, PRIV: 1, GEOB: 1,
        ENCODER: 1, ENCODEDBY: 1, 'ENCODER SETTINGS': 1, COMMENT: 1, DESCRIPTION: 1,
        SOFTWARE: 1, TOOL: 1, ISFT: 1, ICMT: 1, ITCH: 1,
        '©TOO': 1, '©CMT': 1, '©ENC': 1,
        // ASF (WMA): what encoders and tag writers record about themselves.
        'WM/ENCODINGSETTINGS': 1, 'WM/TOOLNAME': 1, 'WM/TOOLVERSION': 1, 'WM/ENCODEDBY': 1,
        WMFSDKVERSION: 1, WMFSDKNEEDED: 1, ISVBR: 1, MEDIAFOUNDATIONVERSION: 1
    };

    // A machine-readable AI declaration: (key, value-test).
    // digitalsourcetype is the IPTC term the industry (and our own writer) uses.
    var DECLARATIONS = [
        { key: /^digitalsourcetype$/i, value: /trainedalgorithmicmedia|compositewithtrainedalgorithmicmedia/i },
        { key: /^ai[_\s-]?generated$/i, value: /^(true|1|yes)$/i },
        { key: /^(genai|ai)[_\s-]?(provenance|source)$/i, value: /.+/ }
    ];

    function classify(name, key) {
        var n = String(name || '').toUpperCase();
        var k = String(key || '');
        if (k) {
            for (var i = 0; i < DECLARATIONS.length; i++) {
                if (DECLARATIONS[i].key.test(k)) return 'declaration-key';
            }
        }
        if (CONTENT_FIELDS[n]) return 'content';
        if (TOOL_FIELDS[n]) return 'tool';
        // Unknown frame id: treat as tool-written. A human never types into a
        // frame we have no name for; encoders invent private frames all the time.
        return 'tool';
    }

    /* ── binary helpers ─────────────────────────────────────────────────── */

    function u32be(b, o) { return (b[o] * 0x1000000) + (b[o + 1] << 16) + (b[o + 2] << 8) + b[o + 3]; }
    function u32le(b, o) { return b[o] + (b[o + 1] << 8) + (b[o + 2] << 16) + (b[o + 3] * 0x1000000); }
    function synchsafe(b, o) { return ((b[o] & 0x7f) << 21) | ((b[o + 1] & 0x7f) << 14) | ((b[o + 2] & 0x7f) << 7) | (b[o + 3] & 0x7f); }
    function ascii(b, o, n) {
        var s = '';
        for (var i = 0; i < n && o + i < b.length; i++) s += String.fromCharCode(b[o + i]);
        return s;
    }
    // ID3 text encodings: 0 = latin1, 1 = UTF-16 with BOM, 2 = UTF-16BE, 3 = UTF-8.
    // Node's TextDecoder has no bare 'utf-16' alias, so pick the endian explicitly
    // from the BOM — the browser accepts these names too.
    function dec(b, enc) {
        try {
            if (enc === 1) {
                var le = !(b.length >= 2 && b[0] === 0xfe && b[1] === 0xff);
                var body = (b.length >= 2 && (b[0] === 0xff || b[0] === 0xfe)) ? b.subarray(2) : b;
                return new TextDecoder(le ? 'utf-16le' : 'utf-16be', { fatal: false }).decode(body);
            }
            if (enc === 2) return new TextDecoder('utf-16be', { fatal: false }).decode(b);
            if (enc === 0) return new TextDecoder('latin1', { fatal: false }).decode(b);
            // Declared UTF-8 (Vorbis, APE, MP4, ID3v2.4) that is not UTF-8: an old tagger put the
            // system code page there. Read it one byte per character — reversible, so the text can
            // be read again in its real encoding (tag-text-encoding.js) — instead of replacement characters (U+FFFD).
            try { return new TextDecoder('utf-8', { fatal: true }).decode(b); }
            catch (notUtf8) { return new TextDecoder('latin1', { fatal: false }).decode(b); }
        } catch (e) {
            return ascii(b, 0, b.length);
        }
    }
    function clean(s) { return String(s || '').replace(/\u0000+$/g, '').replace(/\u0000/g, ' ').trim(); }

    /* ── ID3v2 (mp3 head, also embedded in wav/aiff `id3 ` chunks) ──────── */

    function readId3v2(b, base) {
        var out = [];
        base = base || 0;
        if (b.length < base + 10 || ascii(b, base, 3) !== 'ID3') return { fields: out, size: 0 };
        var major = b[base + 3];
        var flags = b[base + 5];
        var size = synchsafe(b, base + 6);
        var end = Math.min(base + 10 + size, b.length);
        var p = base + 10;
        if (flags & 0x40) {                       // extended header
            if (major >= 4) p += synchsafe(b, p);
            else p += 4 + u32be(b, p);
        }
        var idLen = major === 2 ? 3 : 4;
        var hdrLen = major === 2 ? 6 : 10;
        while (p + hdrLen <= end) {
            var id = ascii(b, p, idLen);
            if (!/^[A-Z0-9]{3,4}$/.test(id)) break;   // padding or garbage → stop
            var fsize = major === 2 ? ((b[p + 3] << 16) | (b[p + 4] << 8) | b[p + 5])
                      : major === 4 ? synchsafe(b, p + 4)
                      : u32be(b, p + 4);
            if (fsize <= 0 || p + hdrLen + fsize > end) break;
            var body = b.subarray(p + hdrLen, p + hdrLen + fsize);
            emitId3Frame(out, id, body);
            p += hdrLen + fsize;
        }
        return { fields: out, size: 10 + size };
    }

    function emitId3Frame(out, id, body) {
        if (!body.length) return;
        var enc = body[0];
        var rest = body.subarray(1);
        if (id === 'TXXX' || id === 'TXX') {
            // description \0 value — the description is the KEY (e.g. digitalsourcetype)
            var txt = dec(rest, enc);
            var parts = txt.split('\u0000');
            var key = clean(parts[0]);
            var val = clean(parts.slice(1).join(' '));
            out.push({ id: id, name: id, key: key, value: val, cls: classify(id, key) });
            return;
        }
        if (id === 'COMM' || id === 'COM') {
            // enc + 3-byte language + short-desc \0 + text
            var t = dec(rest.subarray(3), enc);
            var pr = t.split('\u0000');
            out.push({ id: id, name: id, key: clean(pr[0]), value: clean(pr.slice(1).join(' ')), cls: 'tool' });
            return;
        }
        if (id === 'PRIV') {
            var pt = dec(rest, 3).split('\u0000');
            out.push({ id: id, name: id, key: clean(pt[0]), value: clean(pt.slice(1).join(' ')), cls: 'tool' });
            return;
        }
        if (id.charAt(0) === 'T' || id.charAt(0) === 'W') {
            out.push({ id: id, name: id, key: '', value: clean(dec(rest, enc)), cls: classify(id, '') });
        }
    }

    /* ── ID3v1 (last 128 bytes) ─────────────────────────────────────────── */

    function readId3v1(tail) {
        var out = [];
        if (!tail || tail.length < 128) return out;
        var o = tail.length - 128;
        if (ascii(tail, o, 3) !== 'TAG') return out;
        var f = [['TITLE', 3, 30], ['ARTIST', 33, 30], ['ALBUM', 63, 30], ['COMMENT', 97, 30]];
        for (var i = 0; i < f.length; i++) {
            var v = clean(dec(tail.subarray(o + f[i][1], o + f[i][1] + f[i][2]), 0));
            if (v) out.push({ id: f[i][0], name: f[i][0], key: '', value: v, cls: classify(f[i][0], '') });
        }
        return out;
    }

    /* ── APEv2 (tail, before/after ID3v1) ───────────────────────────────── */

    function readApe(tail) {
        var out = [];
        if (!tail) return out;
        var at = -1;
        for (var i = tail.length - 32; i >= 0; i--) {
            if (ascii(tail, i, 8) === 'APETAGEX') { at = i; break; }
        }
        if (at < 0) return out;
        var size = u32le(tail, at + 12);
        var count = u32le(tail, at + 16);
        // footer sits at `at`; items precede it
        var p = at - (size - 32);
        if (p < 0) p = at + 32;                   // header form: items follow
        for (var n = 0; n < count && p + 8 < tail.length; n++) {
            var vlen = u32le(tail, p);
            p += 8;
            var ks = p;
            while (p < tail.length && tail[p] !== 0) p++;
            var key = ascii(tail, ks, p - ks);
            p++;
            var val = clean(dec(tail.subarray(p, p + vlen), 3));
            p += vlen;
            if (key) out.push({ id: key, name: key, key: '', value: val, cls: classify(key, key) });
        }
        return out;
    }

    /* ── Vorbis comment (flac / ogg) ────────────────────────────────────── */

    function readVorbisComments(b, start, limit) {
        var out = [];
        var p = start;
        if (p + 4 > b.length) return out;
        var vlen = u32le(b, p); p += 4 + vlen;     // vendor string
        if (p + 4 > b.length) return out;
        var count = u32le(b, p); p += 4;
        for (var i = 0; i < count && p + 4 <= b.length && (!limit || p < limit); i++) {
            var len = u32le(b, p); p += 4;
            if (len < 0 || p + len > b.length) break;
            var kv = dec(b.subarray(p, p + len), 3);
            p += len;
            var eq = kv.indexOf('=');
            if (eq < 1) continue;
            var key = kv.slice(0, eq);
            out.push({ id: key, name: key, key: key, value: clean(kv.slice(eq + 1)), cls: classify(key, key) });
        }
        return out;
    }

    function readFlac(b) {
        var out = [];
        if (ascii(b, 0, 4) !== 'fLaC') return out;
        var p = 4;
        while (p + 4 <= b.length) {
            var last = b[p] & 0x80;
            var type = b[p] & 0x7f;
            var len = (b[p + 1] << 16) | (b[p + 2] << 8) | b[p + 3];
            p += 4;
            if (type === 4) return readVorbisComments(b, p, p + len);
            p += len;
            if (last) break;
        }
        return out;
    }

    function readOgg(b) {
        // find the "\x03vorbis" comment header inside the first pages
        for (var i = 0; i + 7 < b.length && i < 65536; i++) {
            if (b[i] === 0x03 && ascii(b, i + 1, 6) === 'vorbis') return readVorbisComments(b, i + 7, 0);
            if (ascii(b, i, 8) === 'OpusTags') return readVorbisComments(b, i + 8, 0);
        }
        return [];
    }

    /* ── RIFF / WAV: LIST-INFO chunk and embedded id3 chunk ─────────────── */

    function readRiff(b) {
        var out = [];
        if (ascii(b, 0, 4) !== 'RIFF') return out;
        var p = 12;
        while (p + 8 <= b.length) {
            var id = ascii(b, p, 4);
            var size = u32le(b, p + 4);
            if (size < 0 || size > b.length) break;
            var body = p + 8;
            if (id === 'LIST' && ascii(b, body, 4) === 'INFO') {
                readInfoList(b, body, size, out);
            } else if (id.toLowerCase() === 'id3 ') {
                out = out.concat(readId3v2(b, body).fields);
            }
            p = body + size + (size & 1);
        }
        return out;
    }

    function readInfoList(b, body, size, out) {
        var q = body + 4, endq = Math.min(body + size, b.length);
        while (q + 8 <= endq) {
            var cid = ascii(b, q, 4);
            var clen = u32le(b, q + 4);
            var val = clean(dec(b.subarray(q + 8, Math.min(q + 8 + clen, b.length)), 3));
            if (cid.trim()) out.push({ id: cid, name: cid, key: '', value: val, cls: classify(cid, '') });
            q += 8 + clen + (clen & 1);
        }
    }

    /* ── MP4 / M4A: moov → udta → meta → ilst ───────────────────────────── */

    function readMp4(b) {
        var out = [];
        function walk(start, end, depth) {
            var p = start;
            while (p + 8 <= end && depth < 8) {
                var size = u32be(b, p);
                var type = ascii(b, p + 4, 4);
                if (size < 8) return;
                var bodyStart = p + 8;
                var bodyEnd = Math.min(p + size, end);
                if (type === 'moov' || type === 'udta' || type === 'trak' || type === 'mdia') {
                    walk(bodyStart, bodyEnd, depth + 1);
                } else if (type === 'meta') {
                    walk(bodyStart + 4, bodyEnd, depth + 1);   // meta has a 4-byte version
                } else if (type === 'ilst') {
                    readIlst(bodyStart, bodyEnd, out);
                }
                p += size;
            }
        }
        function readIlst(start, end, acc) {
            var p = start;
            while (p + 8 <= end) {
                var size = u32be(b, p);
                if (size < 8) return;
                var key = ascii(b, p + 4, 4);
                var q = p + 8, name = key;
                if (key === '----') {                    // freeform: mean/name/data
                    var sub = q, nm = '';
                    while (sub + 8 <= p + size) {
                        var ssz = u32be(b, sub);
                        var st = ascii(b, sub + 4, 4);
                        if (ssz < 8) break;
                        if (st === 'name') nm = clean(dec(b.subarray(sub + 12, sub + ssz), 3));
                        if (st === 'data') {
                            acc.push({
                                id: '----', name: nm || '----', key: nm,
                                value: clean(dec(b.subarray(sub + 16, sub + ssz), 3)),
                                cls: classify(nm, nm)
                            });
                        }
                        sub += ssz;
                    }
                } else {
                    while (q + 8 <= p + size) {
                        var dsz = u32be(b, q);
                        var dt = ascii(b, q + 4, 4);
                        if (dsz < 8) break;
                        if (dt === 'data') {
                            acc.push({
                                id: key, name: name, key: '',
                                value: clean(dec(b.subarray(q + 16, q + dsz), 3)),
                                cls: classify(key, '')
                            });
                        }
                        q += dsz;
                    }
                }
                p += size;
            }
        }
        walk(0, b.length, 0);
        return out;
    }

    /* ── ASF / WMA: Content Description, Extended Content Description,
          Metadata and Metadata Library (inside the Header Extension) ─────── */

    // A GUID as it sits in the file: the first three fields little-endian.
    function asfGuid(s) {
        var h = s.replace(/-/g, ''), b = [], i;
        for (i = 3; i >= 0; i--) b.push(parseInt(h.substr(i * 2, 2), 16));
        for (i = 1; i >= 0; i--) b.push(parseInt(h.substr(8 + i * 2, 2), 16));
        for (i = 1; i >= 0; i--) b.push(parseInt(h.substr(12 + i * 2, 2), 16));
        for (i = 0; i < 8; i++) b.push(parseInt(h.substr(16 + i * 2, 2), 16));
        return b;
    }
    var ASF = {
        header: asfGuid('75B22630-668E-11CF-A6D9-00AA0062CE6C'),
        cdo: asfGuid('75B22633-668E-11CF-A6D9-00AA0062CE6C'),
        ecd: asfGuid('D2D0A440-E307-11D2-97F0-00A0C95EA850'),
        ext: asfGuid('5FBF03B5-A92E-11CF-8EE3-00C00C205365'),
        meta: asfGuid('C5F8CBEA-5BAF-4877-8467-AA8C44FA4CCA'),
        metalib: asfGuid('44231C94-9498-49D1-A141-1D134E457054')
    };
    function asfIs(b, at, g) {
        if (at + 16 > b.length) return false;
        for (var i = 0; i < 16; i++) if (b[at + i] !== g[i]) return false;
        return true;
    }
    function u16le(b, o) { return b[o] | (b[o + 1] << 8); }
    function u64le(b, o) { return u32le(b, o) + u32le(b, o + 4) * 4294967296; }
    function utf16leZ(b, at, len) {
        var s = '';
        for (var i = 0; i + 1 < len && at + i + 1 < b.length; i += 2) {
            var c = b[at + i] | (b[at + i + 1] << 8);
            if (c === 0) break;
            s += String.fromCharCode(c);
        }
        return s;
    }
    // Value types: 0 string, 1 bytes, 2 BOOL, 3 DWORD, 4 QWORD, 5 WORD, 6 GUID.
    function asfValue(b, type, at, len) {
        if (type === 0) return utf16leZ(b, at, len);
        if (type === 3 && len >= 4) return String(u32le(b, at));
        if (type === 4 && len >= 8) return String(u64le(b, at));
        if (type === 5 && len >= 2) return String(u16le(b, at));
        if (type === 2 && len >= 2) return (b[at] || b[at + 1]) ? 'true' : 'false';
        return null;                                   // pictures and GUIDs are not text
    }
    function asfObjects(b, start, stop, visit) {
        var p = start;
        while (p + 24 <= stop) {
            var size = u64le(b, p + 16);
            if (size < 24 || p + size > stop) return;
            visit(p, size);
            p += size;
        }
    }
    /** Header size from the first 24 bytes, 0 when this is not ASF. */
    function asfHeaderSize(b) {
        return (b && b.length >= 30 && asfIs(b, 0, ASF.header)) ? u64le(b, 16) : 0;
    }
    function readAsf(b) {
        var out = [];
        var hsz = asfHeaderSize(b);
        if (!hsz) return out;
        var end = Math.min(b.length, hsz);
        function push(name, text) {
            var v = clean(text);
            if (name && v) out.push({ id: name, name: name, key: '', value: v, cls: classify(name, '') });
        }
        function records(p, size) {                   // Metadata / Metadata Library
            var n = u16le(b, p + 24), q = p + 26, stop = Math.min(end, p + size);
            for (var i = 0; i < n && q + 12 <= stop; i++) {
                var nl = u16le(b, q + 4), type = u16le(b, q + 6), dl = u32le(b, q + 8);
                var v = q + 12 + nl;
                if (v + dl > stop) return;
                var text = asfValue(b, type, v, dl);
                if (text !== null) push(utf16leZ(b, q + 12, nl), text);
                q = v + dl;
            }
        }
        asfObjects(b, 30, end, function (p, size) {
            var stop = Math.min(end, p + size);
            if (asfIs(b, p, ASF.cdo)) {
                var names = ['Title', 'Author', 'Copyright', 'Description', 'Rating'];
                var q = p + 34;
                for (var i = 0; i < 5; i++) {
                    var len = u16le(b, p + 24 + i * 2);
                    if (q + len > stop) return;
                    push(names[i], utf16leZ(b, q, len));
                    q += len;
                }
            } else if (asfIs(b, p, ASF.ecd)) {
                var n = u16le(b, p + 24), r = p + 26;
                for (var j = 0; j < n && r + 6 <= stop; j++) {
                    var nl = u16le(b, r), type = u16le(b, r + 2 + nl), vl = u16le(b, r + 4 + nl);
                    var v = r + 6 + nl;
                    if (v + vl > stop) return;
                    var text = asfValue(b, type, v, vl);
                    if (text !== null) push(utf16leZ(b, r + 2, nl), text);
                    r = v + vl;
                }
            } else if (asfIs(b, p, ASF.ext)) {
                var d = p + 46, ds = u32le(b, p + 42);
                if (d + ds <= stop) {
                    asfObjects(b, d, d + ds, function (c, cs) {
                        if (asfIs(b, c, ASF.meta) || asfIs(b, c, ASF.metalib)) records(c, cs);
                    });
                }
            }
        });
        return out;
    }

    /* ── format sniff + public API ──────────────────────────────────────── */

    function sniff(b) {
        if (!b || b.length < 12) return 'unknown';
        if (b.length >= 30 && asfIs(b, 0, ASF.header)) return 'asf';
        if (ascii(b, 0, 3) === 'ID3') return 'mp3';
        if (ascii(b, 0, 4) === 'fLaC') return 'flac';
        if (ascii(b, 0, 4) === 'OggS') return 'ogg';
        if (ascii(b, 0, 4) === 'RIFF') return 'wav';
        if (ascii(b, 4, 4) === 'ftyp') return 'mp4';
        if (ascii(b, 0, 4) === 'FORM') return 'aiff';
        if (b[0] === 0xff && (b[1] & 0xe0) === 0xe0) return 'mp3';
        return 'unknown';
    }

    /**
     * head / tail: Uint8Array slices of the file (tail may be empty).
     * Returns { format, fields:[{id,name,key,value,cls}] }.
     * `cls` is 'declaration-key' | 'tool' | 'content'.
     */
    function readBytes(head, tail) {
        var fmt = sniff(head);
        var fields = [];
        try {
            if (fmt === 'mp3') fields = readId3v2(head, 0).fields;
            else if (fmt === 'flac') fields = readFlac(head);
            else if (fmt === 'ogg') fields = readOgg(head);
            else if (fmt === 'wav') fields = readRiff(head);
            else if (fmt === 'mp4') fields = readMp4(head);
            else if (fmt === 'asf') fields = readAsf(head);
        } catch (e) { /* a malformed tag must never break the caller */ }
        try {
            if (tail && tail.length) {
                if (fmt === 'mp3' || fmt === 'unknown') {
                    fields = fields.concat(readId3v1(tail)).concat(readApe(tail));
                } else if (fmt === 'mp4' && !fields.length) {
                    /* moov is often at the tail — but the tail slice starts at an
                       arbitrary byte, and a box walk from there reads garbage for
                       a size and stops at once. Until 2026-09-23 that is exactly
                       what happened: every M4A without faststart (FFmpeg's default
                       output, most phone recorders) came back with NO tags. Align
                       the walk on the 'moov' box header first. */
                    fields = fields.concat(readMp4(alignOnMoov(tail)));
                }
            }
        } catch (e) { /* ditto */ }
        return { format: fmt, fields: fields };
    }

    /** The slice from the 'moov' box header on, or the slice itself if absent. */
    function alignOnMoov(b) {
        for (var i = 4; i + 4 <= b.length; i++) {
            if (b[i] === 0x6d && b[i + 1] === 0x6f && b[i + 2] === 0x6f && b[i + 3] === 0x76 && u32be(b, i - 4) >= 8) {
                return b.subarray(i - 4);
            }
        }
        return b;
    }

    /**
     * Tags of a RIFF/WAVE or FORM/AIFF file wherever their chunks are. The
     * ID3 chunk — and often LIST/INFO — sits AFTER the audio data (FFmpeg,
     * foobar2000, Mp3tag all write it there), and a walk inside the head stops
     * at the data chunk, which is bigger than any head: until 2026-09-23 an
     * AIFF came back with no tags at all and a tagged WAV with none of its ID3.
     * Walks chunk headers through the file, 8 bytes a step. Resolves the
     * fields, or null when the walk found no tag chunk.
     */
    function readIffTags(file, slice, fmt) {
        var size = file.size || 0;
        var riff = fmt === 'wav';
        var fields = [], found = false;
        function step(p, guard) {
            if (p + 8 > size || guard > 64) return Promise.resolve(found ? fields : null);
            return slice(p, p + 8).then(function (h) {
                var id = ascii(h, 0, 4);
                var len = riff ? u32le(h, 4) : u32be(h, 4);
                if (!/^[\x20-\x7e]{4}$/.test(id)) return found ? fields : null;
                var next = p + 8 + len + (len & 1);
                var wanted = id.toLowerCase() === 'id3 ' || (riff && id === 'LIST');
                if (!wanted || len > 16 * 1024 * 1024) return step(next, guard + 1);
                return slice(p + 8, Math.min(size, p + 8 + len)).then(function (body) {
                    if (id === 'LIST') {
                        if (ascii(body, 0, 4) === 'INFO') { readInfoList(body, 0, len, fields); found = true; }
                    } else if (ascii(body, 0, 3) === 'ID3') {
                        fields = fields.concat(readId3v2(body, 0).fields);
                        found = true;
                    }
                    return step(next, guard + 1);
                });
            });
        }
        return step(12, 0);
    }

    /**
     * Find the top-level 'moov' box of an MP4 by walking box headers (16 bytes
     * a step — ftyp, free, mdat…), and return its bytes. Exact where a tail
     * slice is a guess: a long file's moov, or one carrying a large cover, is
     * bigger than any fixed tail. Resolves null when there is no moov within
     * the walk or it is implausibly large.
     */
    function locateMoov(file, slice) {
        var size = file.size || 0;
        var MAX_MOOV = 32 * 1024 * 1024;
        function step(p, guard) {
            if (p + 8 > size || guard > 64) return Promise.resolve(null);
            return slice(p, Math.min(size, p + 16)).then(function (h) {
                var len = u32be(h, 0);
                var type = ascii(h, 4, 4);
                var hdr = 8;
                if (len === 1 && h.length >= 16) { len = u32be(h, 8) * 4294967296 + u32be(h, 12); hdr = 16; }
                else if (len === 0) len = size - p;
                if (len < hdr) return null;
                if (type === 'moov') return len > MAX_MOOV ? null : slice(p, Math.min(size, p + len));
                return step(p + len, guard + 1);
            });
        }
        return step(0, 0);
    }

    function read(file, opts) {
        opts = opts || {};
        var headBytes = opts.headBytes || 256 * 1024;
        var tailBytes = opts.tailBytes || 64 * 1024;
        function slice(start, end) {
            return new Promise(function (res, rej) {
                var fr = new FileReader();
                fr.onload = function () { res(new Uint8Array(fr.result)); };
                fr.onerror = function () { rej(fr.error); };
                fr.readAsArrayBuffer(file.slice(start, end));
            });
        }
        return slice(0, Math.min(headBytes, file.size)).then(function (head) {
            var whole = file.size <= headBytes;
            var tailRead = function () {
                if (whole) return readBytes(head, head);
                return slice(Math.max(0, file.size - tailBytes), file.size).then(function (tail) {
                    return readBytes(head, tail);
                });
            };
            var fmt = sniff(head);
            if (fmt === 'asf') {
                /* Every WMA tag lives in the header object, whose size is in
                   its first 24 bytes. A cover there can be megabytes, so read
                   exactly the header — never the audio after it. */
                var hsz = asfHeaderSize(head);
                if (hsz > head.length && hsz <= 64 * 1024 * 1024 && hsz <= file.size) {
                    return slice(0, hsz).then(function (h) { return readBytes(h, null); });
                }
                return readBytes(head, null);
            }
            if (fmt === 'wav' || fmt === 'aiff') {
                return readIffTags(file, slice, fmt).then(function (fields) {
                    return fields ? { format: fmt, fields: fields } : tailRead();
                }, tailRead);
            }
            if (whole) return readBytes(head, head);
            if (fmt === 'mp4') {
                var first = readBytes(head, null);
                if (first.fields.length) return first;                 // faststart: moov in the head
                return locateMoov(file, slice).then(function (moov) {
                    return moov ? readBytes(head, moov) : tailRead();
                }, tailRead);
            }
            return tailRead();
        });
    }

    return {
        read: read,
        readBytes: readBytes,
        sniff: sniff,
        CONTENT_FIELDS: CONTENT_FIELDS,
        TOOL_FIELDS: TOOL_FIELDS,
        DECLARATIONS: DECLARATIONS
    };
});
